import { useTranslations } from 'use-intl';
import type { TaxAccountUnavailableReason } from '@financialharmony/ui/tax/rowModel';

type ReasonCopy = Record<TaxAccountUnavailableReason, string>;

/**
 * Why a tax account has no figure, in the language the app shows: the short sentence a row in the
 * accounts list carries, the short badge wording of the detail page, and the detail page's longer
 * explanation. The authority is named from the registry, never hardcoded.
 */
export function useTaxAccountReasons() {
  const t = useTranslations('consultantTax.reason');
  const tNoAccount = useTranslations('tax.noAccount');

  const shared = (authority: string) => ({
    insufficient_scope: t('shared.insufficient_scope'),
    not_readable: t('shared.not_readable'),
    revoked: t('shared.revoked'),
    incomplete_data: t('shared.incomplete_data'),
    closed: t('shared.closed', { authority }),
  });

  return {
    row: (authority: string): ReasonCopy => ({
      not_connected: t('row.not_connected'),
      cancelled: t('row.cancelled', { authority }),
      pending: t('row.pending', { authority }),
      no_tax_account: tNoAccount('row', { authority }),
      ...shared(authority),
    }),
    badge: (authority: string): ReasonCopy => ({
      not_connected: t('badge.not_connected'),
      cancelled: t('badge.cancelled', { authority }),
      pending: t('badge.pending'),
      no_tax_account: tNoAccount('row', { authority }),
      ...shared(authority),
    }),
    explanation: (authority: string): ReasonCopy => ({
      not_connected: t('explanation.not_connected'),
      cancelled: t('explanation.cancelled', { authority }),
      pending: t('explanation.pending', { authority }),
      no_tax_account: tNoAccount('explanation', { authority }),
      insufficient_scope: t('explanation.insufficient_scope'),
      not_readable: t('explanation.not_readable', { authority }),
      revoked: t('explanation.revoked', { authority }),
      incomplete_data: t('explanation.incomplete_data', { authority }),
      closed: t('explanation.closed', { authority }),
    }),
  };
}
